import React from "react";
import { TRUST } from "@/constants/testIds";
import { Ornament, Logo } from "@/components/Logo";
import { ShieldCheck, ScrollText, LockKeyhole } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrustSection() {
  return (
    <section
      id="collectors"
      data-testid={TRUST.root}
      className="relative paper-texture py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] tracking-[0.5em] uppercase text-[#722F37]">
            What Collectors Receive
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight font-light">
            More than a painting.
          </h2>
          <div className="mx-auto mt-6 w-40">
            <Ornament color="#D4AF37" />
          </div>
          <p className="mt-6 text-base text-[#1A1A1A]/70">
            Every original from the House of Master Brushes Fine Art travels
            with the same three assurances. They are non-negotiable.
          </p>
        </div>

        {/* Feature row */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          {[
            {
              icon: ScrollText,
              title: "Certificate of Authenticity",
              body: "Numbered and signed by Tom Pergola as Master Artist. Attests the work is an original from his studio hand.",
            },
            {
              icon: ShieldCheck,
              title: "Letter of Appreciation",
              body: "A personal note co-signed by Tom Pergola and National Coordinator Edgar Hicks — carried with each work into your collection.",
            },
            {
              icon: LockKeyhole,
              title: "Escrow.com secured payment",
              body: "Reservation funds are held by Escrow.com until you have received the work. You pay only when you are satisfied.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="relative bg-white p-8 border border-[#D4AF37]/30"
            >
              <div className="absolute -top-3 left-8 bg-white px-2">
                <f.icon
                  size={22}
                  strokeWidth={1.4}
                  color="#722F37"
                />
              </div>
              <h3 className="font-serif text-2xl tracking-tight mt-2">
                {f.title}
              </h3>
              <p className="mt-3 text-sm text-[#1A1A1A]/70 leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>

        {/* PDF previews */}
        <div className="mt-20 grid md:grid-cols-2 gap-10 items-stretch">
          <PreviewCard
            id={TRUST.certificatePreview}
            title="Preview · Certificate of Authenticity"
            href={`${BACKEND_URL}/api/preview/certificate.pdf?name=Valued+Collector&title=Sovereign+Landscape`}
            accent="#722F37"
            body={[
              "Attesting this is an original work of art",
              "By the hand of Tom Pergola",
              "Master Artist, House of Master Brushes Fine Art",
            ]}
          />
          <PreviewCard
            id={TRUST.letterPreview}
            title="Preview · Letter of Appreciation"
            href={`${BACKEND_URL}/api/preview/letter.pdf?name=Valued+Collector&title=Sovereign+Landscape`}
            accent="#0B3B24"
            body={[
              "A personal thank-you signed by",
              "Tom Pergola — Master Artist",
              "Edgar Hicks — National Coordinator",
            ]}
          />
        </div>
      </div>
    </section>
  );
}

function PreviewCard({ id, title, href, accent, body }) {
  return (
    <div
      data-testid={id}
      className="relative bg-white border border-[#D4AF37]/50 p-10 md:p-14 flex flex-col items-center text-center"
      style={{
        backgroundImage:
          "radial-gradient(circle at 50% 30%, rgba(212,175,55,0.05), transparent 60%)",
      }}
    >
      <div className="absolute inset-4 border border-[#D4AF37]/40 pointer-events-none" />
      <div className="mb-6">
        <Logo size={68} />
      </div>
      <div
        className="font-serif text-[10px] tracking-[0.45em] uppercase"
        style={{ color: accent }}
      >
        House of Master Brushes Fine Art
      </div>
      <h3
        className="font-serif text-3xl tracking-tight mt-4"
        style={{ color: accent }}
      >
        {title.replace("Preview · ", "")}
      </h3>
      <div className="mt-6 space-y-1 font-serif italic text-[15px] text-[#1A1A1A]/75">
        {body.map((b, i) => (
          <div key={i}>{b}</div>
        ))}
      </div>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="mt-8 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] hover:text-[#B78D20]"
      >
        View sample PDF ↗
      </a>
    </div>
  );
}
